/**
 * DSGVO/GDPR cookie consent banner.
 *
 * Renders globally — but only on routes that are public-facing (landing page,
 * salon-public pages, platform legal pages). Admin/CMS areas are skipped.
 *
 * Consent is stored in localStorage under `studiomv_cookie_consent`:
 *   { version, essential: true, statistics, marketing, ts }
 *
 * Re-opener: any element can call `window.__openCookieConsent()` (the function
 * is exposed by this component on mount). Useful from footer links like
 * "Cookie-Einstellungen".
 */
import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Cookie, X, ShieldCheck, BarChart3, Megaphone } from "lucide-react";

const STORAGE_KEY = "studiomv_cookie_consent";
const CONSENT_VERSION = 1;
const TTL_DAYS = 365;

function loadConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    const age = (Date.now() - new Date(parsed.ts).getTime()) / 86400000;
    if (age > TTL_DAYS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(consent) {
  const doc = { version: CONSENT_VERSION, essential: true, ...consent, ts: new Date().toISOString() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(doc));
  } catch { /* quota / private mode */ }
  // Notify any listeners (e.g. analytics scripts) that consent changed
  window.dispatchEvent(new CustomEvent("studiomv:consent-change", { detail: doc }));
}

// Routes where the banner MUST NOT appear (logged-in admin/CMS surfaces).
const SKIP_PATH_PREFIXES = ["/cms", "/admin", "/__preview__"];
function shouldShow(pathname) {
  return !SKIP_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

export default function CookieConsent() {
  const location = useLocation();
  // null = not yet decided this session; false = decided; true = panel forced open
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ statistics: false, marketing: false });

  // On mount: decide whether to show. Also expose a re-opener on window.
  useEffect(() => {
    if (!shouldShow(location.pathname)) {
      setOpen(false);
      return;
    }
    const existing = loadConsent();
    if (!existing) {
      setOpen(true);
    } else {
      setPrefs({ statistics: !!existing.statistics, marketing: !!existing.marketing });
    }
  }, [location.pathname]);

  // Expose a global handler so legal-page / footer links can trigger the panel.
  useEffect(() => {
    const opener = () => {
      const existing = loadConsent();
      if (existing) setPrefs({ statistics: !!existing.statistics, marketing: !!existing.marketing });
      setShowSettings(true);
      setOpen(true);
    };
    window.__openCookieConsent = opener;
    return () => { if (window.__openCookieConsent === opener) delete window.__openCookieConsent; };
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({ statistics: true, marketing: true });
    setOpen(false);
    setShowSettings(false);
  }, []);

  const rejectAll = useCallback(() => {
    saveConsent({ statistics: false, marketing: false });
    setOpen(false);
    setShowSettings(false);
  }, []);

  const saveSelection = useCallback(() => {
    saveConsent({ statistics: prefs.statistics, marketing: prefs.marketing });
    setOpen(false);
    setShowSettings(false);
  }, [prefs]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[200] p-3 sm:p-5"
      data-testid="cookie-consent-root"
      role="dialog"
      aria-modal="false"
      aria-label="Cookie-Einstellungen"
    >
      <div
        className="mx-auto max-w-3xl rounded-2xl border shadow-2xl"
        style={{
          background: "#ffffff",
          color: "#1f1a16",
          borderColor: "rgba(0,0,0,0.12)",
        }}
      >
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-3">
            <div
              className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: "var(--mv-primary, #C87A5F)", color: "#fff" }}
            >
              <Cookie className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl font-medium mb-1">
                Wir respektieren deine Privatsphäre
              </h2>
              <p className="text-sm leading-relaxed" style={{ color: "rgba(31,26,22,0.72)" }}>
                Wir verwenden Cookies und ähnliche Technologien. Essenzielle Cookies sind nötig,
                damit die Seite funktioniert. Statistik- und Marketing-Cookies helfen uns, das
                Angebot zu verbessern — aber nur, wenn du sie erlaubst. Du kannst deine Auswahl
                jederzeit über den Link &bdquo;Cookie-Einstellungen&ldquo; im Footer ändern.
              </p>
              <p className="text-xs mt-2" style={{ color: "rgba(31,26,22,0.55)" }}>
                Mehr in unserer{" "}
                <a href="/datenschutz" className="underline" data-testid="cookie-privacy-link">
                  Datenschutzerklärung
                </a>
                .
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowSettings((s) => !s)}
              className="hidden sm:inline-flex items-center text-xs underline opacity-70 hover:opacity-100"
              data-testid="cookie-toggle-settings"
            >
              {showSettings ? "Weniger anzeigen" : "Einstellungen anpassen"}
            </button>
          </div>

          {showSettings && (
            <div className="mt-4 border-t pt-4 space-y-3" style={{ borderColor: "rgba(0,0,0,0.08)" }} data-testid="cookie-settings-panel">
              <CategoryRow
                icon={<ShieldCheck className="w-4 h-4" strokeWidth={1.7} />}
                title="Essenziell"
                description="Notwendig für Login, Buchungen und Sicherheits-Features. Diese Cookies sind immer aktiv."
                checked={true}
                disabled
                testid="cookie-cat-essential"
              />
              <CategoryRow
                icon={<BarChart3 className="w-4 h-4" strokeWidth={1.7} />}
                title="Statistik"
                description="Anonyme Auswertungen darüber, wie unsere Seite genutzt wird, helfen uns sie zu verbessern."
                checked={prefs.statistics}
                onChange={(v) => setPrefs((p) => ({ ...p, statistics: v }))}
                testid="cookie-cat-statistics"
              />
              <CategoryRow
                icon={<Megaphone className="w-4 h-4" strokeWidth={1.7} />}
                title="Marketing"
                description="Werden für personalisierte Inhalte und Werbung auf externen Plattformen verwendet."
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                testid="cookie-cat-marketing"
              />
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-stretch">
            {/* DSGVO: reject + accept are equally prominent. */}
            <button
              type="button"
              onClick={rejectAll}
              className="flex-1 px-4 py-2.5 rounded-full border text-sm font-medium"
              style={{ borderColor: "rgba(0,0,0,0.2)", background: "transparent", color: "#1f1a16" }}
              data-testid="cookie-reject-all-btn"
            >
              Nur essenzielle
            </button>
            {showSettings && (
              <button
                type="button"
                onClick={saveSelection}
                className="flex-1 px-4 py-2.5 rounded-full border text-sm font-medium"
                style={{ borderColor: "var(--mv-primary, #C87A5F)", background: "transparent", color: "var(--mv-primary, #C87A5F)" }}
                data-testid="cookie-save-selection-btn"
              >
                Auswahl speichern
              </button>
            )}
            <button
              type="button"
              onClick={acceptAll}
              className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium"
              style={{ background: "var(--mv-primary, #C87A5F)", color: "#fff" }}
              data-testid="cookie-accept-all-btn"
            >
              Alle akzeptieren
            </button>
          </div>

          {/* Mobile: settings toggle below */}
          <button
            type="button"
            onClick={() => setShowSettings((s) => !s)}
            className="sm:hidden mt-3 text-xs underline opacity-70 w-full text-center"
            data-testid="cookie-toggle-settings-mobile"
          >
            {showSettings ? "Weniger anzeigen" : "Einstellungen anpassen"}
          </button>

          {/* Close shortcut for re-opened panels (only when consent already exists). */}
          {loadConsent() && (
            <button
              type="button"
              onClick={() => { setOpen(false); setShowSettings(false); }}
              className="absolute top-3 right-3 p-1 opacity-50 hover:opacity-100"
              aria-label="Schließen"
              data-testid="cookie-close-btn"
            >
              <X className="w-4 h-4" strokeWidth={1.7} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ icon, title, description, checked, onChange, disabled, testid }) {
  return (
    <label
      className="flex gap-3 items-start cursor-pointer select-none"
      data-testid={testid}
      style={{ opacity: disabled ? 0.75 : 1 }}
    >
      <div className="shrink-0 mt-0.5" style={{ color: "var(--mv-primary, #C87A5F)" }}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-medium">{title}</p>
          <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange && onChange(e.target.checked)}
            className="w-4 h-4"
            data-testid={`${testid}-checkbox`}
          />
        </div>
        <p className="text-xs mt-0.5" style={{ color: "rgba(31,26,22,0.6)" }}>
          {description}
        </p>
      </div>
    </label>
  );
}
